// `defineError` (plan §4.5): a typed, coded failure an operation can return. `message` is an i18n key, never prose.
import type { z } from 'zod';

export type HttpErrorStatus = 400 | 401 | 403 | 404 | 409 | 410 | 422 | 429 | 500 | 503;

export interface ErrorDef<C extends string = string, D extends z.ZodType = z.ZodType> {
  readonly kind: 'error';
  readonly code: C;
  readonly httpStatus: HttpErrorStatus;
  /** An i18n message key (`errors.<code>`), never text. */
  readonly message: string;
  readonly retryable: boolean;
  readonly details: D;
}

export function defineError<const C extends string, D extends z.ZodType>(def: Omit<ErrorDef<C, D>, 'kind'>): ErrorDef<C, D> {
  return { kind: 'error', ...def };
}

/** The typed error a caller can receive from a list of ErrorDefs: one member per def, discriminated by `code`. */
export type ErrorOf<E extends readonly ErrorDef[]> = {
  [K in keyof E]: E[K] extends ErrorDef<infer C, infer D>
    ? { readonly code: C; readonly retryable: boolean; readonly details: z.output<D> }
    : never;
}[number];
